/** Build { value, label } options for Ant Select from API / enum shapes */
function normalizeToSelectOptions(
  raw: unknown
): { value: string; label: string }[] {
  if (raw === null) {
    return [];
  }
  const list = Array.isArray(raw) ? raw : [];
  const out: { value: string; label: string }[] = [];
  for (const item of list) {
    if (item === null) {
      continue;
    }
    if (typeof item === "string" || typeof item === "number") {
      const s = String(item);
      out.push({ value: s, label: s });
      continue;
    }
    if (typeof item === "object") {
      const o = item as Record<string, unknown>;
      const value = o.value ?? o.id ?? o.key ?? o.pk;
      const label = o.label ?? o.name ?? o.title ?? o.display_name ?? value;
      if (value !== undefined && value !== null && String(value) !== "") {
        out.push({ value: String(value), label: String(label ?? value) });
      }
    }
  }
  return out;
}

function statusOptionsFromGridField(
  grid: any[] | undefined,
  statusFieldName: string
): { value: string; label: string }[] {
  if (!grid?.length) {
    return [];
  }
  const field = grid.find(
    (f: any) =>
      String(f.field_name ?? "").toLowerCase() ===
      String(statusFieldName).toLowerCase()
  );
  if (!field) {
    return [];
  }
  const candidates = [
    field.enum,
    field.enum_values,
    field.choices,
    field.options,
    field.field_options,
    field.drop_down_values,
    field.validation?.enum,
    field.json_schema?.enum,
    field.x_enum,
  ];
  for (const c of candidates) {
    const opts = normalizeToSelectOptions(c);
    if (opts.length > 0) {
      return opts;
    }
  }
  return [];
}

function parseSchemaJsonIfNeeded(raw: unknown): unknown {
  if (raw === null) {
    return null;
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  return raw;
}

function getFormilySchemaTreeRoot(schemaRecord: unknown): unknown {
  if (schemaRecord === null || typeof schemaRecord !== "object") {
    return null;
  }
  const rec = schemaRecord as Record<string, unknown>;
  let blob: unknown =
    rec.json !== null && typeof rec.json !== "undefined"
      ? rec.json
      : schemaRecord;
  blob = parseSchemaJsonIfNeeded(blob);
  if (blob === null || typeof blob !== "object") {
    return null;
  }
  const b = blob as Record<string, unknown>;
  if (b.schema && typeof b.schema === "object") {
    return b.schema;
  }
  if (b.type === "object" && b.properties && typeof b.properties === "object") {
    return b;
  }
  return blob;
}

/** Walk Formily designable JSON and read inline `enum` on the node with `name` === fieldName */
function walkFormilyForFieldEnum(
  node: unknown,
  fieldName: string
): { value: string; label: string }[] {
  if (node === null || typeof node !== "object") {
    return [];
  }
  const o = node as Record<string, unknown>;

  if (typeof o.name === "string" && o.name === fieldName) {
    const opts = normalizeToSelectOptions(o.enum);
    if (opts.length > 0) {
      return opts;
    }
  }

  const props = o.properties;
  if (props && typeof props === "object" && !Array.isArray(props)) {
    for (const key of Object.keys(props as Record<string, unknown>)) {
      const child = (props as Record<string, unknown>)[key];
      const found = walkFormilyForFieldEnum(child, fieldName);
      if (found.length > 0) {
        return found;
      }
    }
  }
  return [];
}

function statusOptionsFromFormilySchema(
  schemaRecord: unknown,
  fieldName: string
): { value: string; label: string }[] {
  const root = getFormilySchemaTreeRoot(schemaRecord);
  if (!root) {
    return [];
  }
  return walkFormilyForFieldEnum(root, fieldName);
}

export function buildSpentHoursStatusOptions(
  schemaRecord: unknown,
  grid: any[] | undefined,
  statusFieldName: string
): { value: string; label: string }[] {
  const fromFormily = statusOptionsFromFormilySchema(
    schemaRecord,
    statusFieldName
  );
  if (fromFormily.length > 0) {
    return fromFormily;
  }
  return statusOptionsFromGridField(grid, statusFieldName);
}
